import React from "react";
import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { ExamDateDto, ExamFormat, OnlinePaymentStatus } from "api";
import { formatDate } from "utils";
import { DashedDivider } from "./Divider";
import { SafetyBox } from "./SafetyBox";
import { PaymentStatusChip } from "./PaymentStatusChip";

export const ExamCard = ({
  exam,
  cta,
  paymentStatus,
  priceOverride,
  currencyOverride,
}: {
  exam?: ExamDateDto;
  cta?: React.ReactNode;
  paymentStatus?: OnlinePaymentStatus;
  priceOverride?: number | null;
  currencyOverride?: string | null;
}) => {
  const { t } = useTranslation();

  // On the confirmation/payment pages the customer-specific amount (k_kwota_do_zaplaty,
  // which an admin may override) is the source of truth, not the generic term price.
  const displayPrice = priceOverride ?? exam?.price;
  const displayCurrency = currencyOverride ?? exam?.currency;

  return (
    <Paper
      sx={{
        width: "100%",
        p: 2,
        background: (theme) => theme.palette.grey[100],
      }}
    >
      <Typography
        variant="body1"
        sx={{ fontWeight: "bold", mb: 2, color: "text.secondary" }}
      >
        {t("examCard.title")}:
      </Typography>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {exam ? exam.examName : <Skeleton width="70%" />}
      </Typography>

      <Paper
        component={Stack}
        elevation={0}
        sx={{ gap: 1, px: 1, py: 2, mb: 2 }}
      >
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Typography>{t("examCard.date")}:</Typography>
          <Typography sx={{ display: "flex", gap: 0.5, textAlign: "right" }}>
            {exam ? (
              <>
                <CalendarTodayIcon fontSize="small" />
                {formatDate(exam.examDate)}
              </>
            ) : (
              <Skeleton width={100} />
            )}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Typography>{t("examCard.speakingTest")}:</Typography>
          <Typography sx={{ display: "flex", gap: 0.5, textAlign: "right" }}>
            {exam ? (
              <>
                <CalendarMonthIcon fontSize="small" /> {exam.speakingDates}
              </>
            ) : (
              <Skeleton width={120} />
            )}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Typography>{t("examCard.city")}:</Typography>
          <Typography sx={{ textAlign: "right" }}>
            {exam ? exam.city : <Skeleton width={80} />}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Typography>{t("examCard.format")}:</Typography>
          <Typography sx={{ textAlign: "right" }}>
            {exam ? (
              exam.examFormat === ExamFormat.Pb ? (
                t("common.paperBased")
              ) : (
                t("common.computerBased")
              )
            ) : (
              <Skeleton width={90} />
            )}
          </Typography>
        </Stack>
        {paymentStatus !== undefined && (
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography>{t("examCard.paymentStatusLabel")}:</Typography>
            <PaymentStatusChip status={paymentStatus} />
          </Stack>
        )}
      </Paper>

      <DashedDivider color="petrol.dark" />

      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          mt: 2,
          mb: 2,
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: "bold", color: "text.secondary" }}>
          {t("examCard.price")}:
        </Typography>
        <Typography sx={{ fontSize: "1.25rem", fontWeight: "bold" }}>
          {displayPrice != null ? (
            <>
              {displayPrice} {displayCurrency}
            </>
          ) : (
            <Skeleton width={70} />
          )}
        </Typography>
      </Stack>
      {cta && <Box sx={{ mb: 2 }}>{cta}</Box>}
      <SafetyBox />
    </Paper>
  );
};
